import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  VStack,
  HStack,
  Spinner,
  Text,
  Heading,
  Alert,
  AlertIndicator,
  Image,
  Link,
  Flex,
  Textarea,
} from "@chakra-ui/react";
import { BsBoxArrowRight } from "react-icons/bs";
import { format } from "date-fns";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import { compareService, SpeciesComparison } from "../api/services/compare.service";
import { authService } from "../api/services/auth.service";
import AppContext from "../core/app-context";
import { birdrImage } from "../user/visual-style";

function isOutOfCreditsError(message: string | null | undefined): boolean {
  return /out of credits|insufficient_quota|credit_balance_exhausted/i.test(message || '');
}

function ComparisonCreditsEmpty() {
  return (
    <VStack align="center" gap={3} py={2}>
      <Image
        src={birdrImage('birdr-stressed.png')}
        alt=""
        width="220px"
        height="167px"
        objectFit="contain"
      />
      <Text fontSize="md" textAlign="center" lineHeight="tall">
        <FormattedMessage
          id="comparison_out_of_credits"
          defaultMessage="O-o, we ran out of AI credits. I develop Birdr in my spare time, and there is no funding behind it right now. If you would like to help it grow, write to me at info@birdr.pro"
        />
      </Text>
    </VStack>
  );
}

function ComparisonContribute({
  species1Id,
  species2Id,
  onClose,
  showDisclaimer = true,
}: {
  species1Id?: number;
  species2Id?: number;
  onClose?: () => void;
  showDisclaimer?: boolean;
}) {
  const navigate = useNavigate();
  const loggedIn = authService.isAuthenticated();
  const [suggestion, setSuggestion] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  if (!species1Id || !species2Id) return null;

  const handleSubmit = async () => {
    const text = suggestion.trim();
    if (!text || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await compareService.submitCommunityComparison(species1Id, species2Id, text);
      setSubmitMessage("saved");
      setSuggestion("");
    } catch (err: any) {
      setSubmitError(err?.message || "Could not save your description.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <VStack align="stretch" gap={2} pt={4} borderTopWidth="1px">
      {showDisclaimer ? (
      <Alert.Root status="warning">
        <AlertIndicator />
        <Text fontSize="sm">
          <FormattedMessage
            id="comparison_ai_disclaimer"
            defaultMessage="This text was generated by AI from several sources. It can contain mistakes."
          />
        </Text>
      </Alert.Root>
      ) : null}
      <Text fontSize="sm" color="gray.700">
        <FormattedMessage
          id="comparison_suggest_note"
          defaultMessage="Birdr is a community project. If you know these birds, you can improve this comparison."
        />
      </Text>
      {loggedIn ? (
        submitMessage ? (
          <Text fontSize="sm" color="green.700" fontWeight="600">
            <FormattedMessage
              id="comparison_submitted"
              defaultMessage="Thanks — your description is live."
            />
          </Text>
        ) : (
          <>
            <Textarea
              value={suggestion}
              onChange={(e) => setSuggestion(e.target.value)}
              placeholder="How would you tell these two species apart?"
              minH="88px"
            />
            {submitError ? (
              <Text fontSize="sm" color="red.600">{submitError}</Text>
            ) : null}
            <Button
              colorPalette="primary"
              size="sm"
              alignSelf="flex-start"
              onClick={() => void handleSubmit()}
              disabled={!suggestion.trim() || submitting}
            >
              {submitting ? (
                <Spinner size="sm" />
              ) : (
                <FormattedMessage
                  id="comparison_suggest_better"
                  defaultMessage="Submit a better description"
                />
              )}
            </Button>
          </>
        )
      ) : (
        <Button
          variant="outline"
          colorPalette="primary"
          size="sm"
          alignSelf="flex-start"
          onClick={() => {
            onClose?.();
            navigate("/login");
          }}
        >
          <FormattedMessage
            id="comparison_login_to_suggest"
            defaultMessage="Log in to suggest a better description."
          />
        </Button>
      )}
    </VStack>
  );
}

function SpeciesRefLinks({ name, code }: { name: string; code?: string }) {
  const trimmed = code?.trim();
  if (!trimmed) return null;
  return (
    <VStack align="start" gap={1}>
      <Text fontWeight="600" fontSize="sm">{name}</Text>
      <HStack gap={3}>
        <Link href={`https://ebird.org/species/${trimmed}`} target="_blank" rel="noopener noreferrer">
          <Flex gap={1} alignItems="center" fontSize="xs">
            eBird <BsBoxArrowRight />
          </Flex>
        </Link>
        <Link href={`https://birdsoftheworld.org/bow/species/${trimmed}/cur/introduction`} target="_blank" rel="noopener noreferrer">
          <Flex gap={1} alignItems="center" fontSize="xs">
            Birds of the World <BsBoxArrowRight />
          </Flex>
        </Link>
      </HStack>
    </VStack>
  );
}

type ComparisonContentProps = {
  species1Id?: number;
  species2Id?: number;
  species1Name?: string;
  species2Name?: string;
  showSpeciesLinks?: boolean;
  onContributeClose?: () => void;
};

export function ComparisonContent({
  species1Id,
  species2Id,
  species1Name,
  species2Name,
  showSpeciesLinks = true,
  onContributeClose,
}: ComparisonContentProps) {
  const { appLanguage } = useContext(AppContext);
  const [comparison, setComparison] = useState<SpeciesComparison | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const locale = appLanguage || 'en';

  useEffect(() => {
    if (!species1Id || !species2Id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    compareService.getComparison(species1Id, species2Id, locale)
      .then((data) => {
        if (!cancelled) setComparison(data);
      })
      .catch((err: any) => {
        if (!cancelled) {
          setError(err?.message || "Failed to generate comparison");
          setComparison(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [species1Id, species2Id, locale]);

  const displayName1 = species1Name || comparison?.species_1_name || "";
  const displayName2 = species2Name || comparison?.species_2_name || "";
  const outOfCredits =
    comparison?.ai_model === 'botw-extract' || isOutOfCreditsError(error);

  if (!species1Id || !species2Id) return null;

  if (loading) {
    return (
      <VStack gap={4} py={8}>
        <Spinner size="lg" />
        <Text>
          <FormattedMessage
            id="comparison_generating"
            defaultMessage="Generating comparison... This may take a moment."
          />
        </Text>
      </VStack>
    );
  }

  if (outOfCredits) {
    return (
      <VStack align="stretch" gap={4}>
        <ComparisonCreditsEmpty />
        <ComparisonContribute
          species1Id={species1Id}
          species2Id={species2Id}
          onClose={onContributeClose}
          showDisclaimer={false}
        />
      </VStack>
    );
  }

  if (error) {
    return (
      <VStack align="stretch" gap={4}>
        <Alert.Root status="error">
          <AlertIndicator />
          <Box>
            <Text fontWeight="bold">Error</Text>
            <Text>{error}</Text>
          </Box>
        </Alert.Root>
        <ComparisonContribute
          species1Id={species1Id}
          species2Id={species2Id}
          onClose={onContributeClose}
        />
      </VStack>
    );
  }

  if (!comparison) return null;

  return (
    <>
      <style>{`
        .comparison-html {
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        .comparison-html p { margin-bottom: 0.5rem; }
        .comparison-html ul, .comparison-html ol {
          list-style: none;
          margin: 0;
          padding: 0;
          margin-bottom: 0.5rem;
        }
        .comparison-html li {
          margin-bottom: 0.25rem;
          padding-left: 0;
        }
        .comparison-html strong { font-weight: bold; }
        .comparison-html em { font-style: italic; }
        .comparison-html h1, .comparison-html h2, .comparison-html h3 {
          font-weight: bold;
          margin-top: 0.75rem;
          margin-bottom: 0.5rem;
        }
      `}</style>

      {showSpeciesLinks ? (
        <HStack gap={4} mb={4} flexWrap="wrap">
          <SpeciesRefLinks name={displayName1} code={comparison.species_1_code} />
          <SpeciesRefLinks name={displayName2} code={comparison.species_2_code} />
        </HStack>
      ) : null}

      <VStack align="stretch" gap={4}>
        {comparison.summary_html && (
          <Box>
            <Heading size="md" mb={2}>
              Summary
            </Heading>
            <Box
              dangerouslySetInnerHTML={{ __html: comparison.summary_html }}
              className="comparison-html"
            />
          </Box>
        )}
        {comparison.identification_tips_html && (
          <Box bg="blue.50" p={4} borderRadius="md" borderWidth="1px" borderColor="blue.200">
            <Heading size="sm" mb={2} color="blue.800">
              Identification Tips
            </Heading>
            <Box
              dangerouslySetInnerHTML={{ __html: comparison.identification_tips_html }}
              className="comparison-html"
              color="blue.900"
            />
          </Box>
        )}

        {comparison.size_comparison_html && (
          <Box>
            <Heading size="sm" mb={2}>
              Size
            </Heading>
            <Box
              dangerouslySetInnerHTML={{ __html: comparison.size_comparison_html }}
              className="comparison-html"
            />
          </Box>
        )}

        {comparison.plumage_comparison_html && (
          <Box>
            <Heading size="sm" mb={2}>
              Plumage
            </Heading>
            <Box
              dangerouslySetInnerHTML={{ __html: comparison.plumage_comparison_html }}
              className="comparison-html"
            />
          </Box>
        )}

        {comparison.behavior_comparison_html && (
          <Box>
            <Heading size="sm" mb={2}>
              Behavior
            </Heading>
            <Box
              dangerouslySetInnerHTML={{ __html: comparison.behavior_comparison_html }}
              className="comparison-html"
            />
          </Box>
        )}

        {comparison.habitat_comparison_html && (
          <Box>
            <Heading size="sm" mb={2}>
              Habitat
            </Heading>
            <Box
              dangerouslySetInnerHTML={{ __html: comparison.habitat_comparison_html }}
              className="comparison-html"
            />
          </Box>
        )}

        {comparison.vocalization_comparison_html && (
          <Box>
            <Heading size="sm" mb={2}>
              Vocalization
            </Heading>
            <Box
              dangerouslySetInnerHTML={{ __html: comparison.vocalization_comparison_html }}
              className="comparison-html"
            />
          </Box>
        )}

        {(comparison.sources_used?.length ?? 0) > 0 ? (
          <Box pt={2} borderTopWidth="1px">
            <Heading size="xs" mb={1}>
              <FormattedMessage id="comparison_sources" defaultMessage="Sources" />
            </Heading>
            <VStack align="stretch" gap={1}>
              {comparison.sources_used!.map((src, i) => (
                <Text key={`${src.source}-${i}`} fontSize="xs" color="gray.600">
                  {src.url ? (
                    <Link href={src.url} target="_blank" rel="noopener noreferrer">
                      {src.attribution || src.source}
                    </Link>
                  ) : (
                    src.attribution || src.source
                  )}
                  {src.license ? ` · ${src.license}` : ''}
                </Text>
              ))}
            </VStack>
          </Box>
        ) : null}

        <Box pt={2} borderTopWidth="1px">
          <Text fontSize="xs" color="gray.600">
            Generated using {comparison.ai_model} on {format(new Date(comparison.generated_at), "PPpp")}
          </Text>
        </Box>
      </VStack>
      <ComparisonContribute
        species1Id={species1Id}
        species2Id={species2Id}
        onClose={onContributeClose}
      />
    </>
  );
}
